/**
 * Pure view model for the pool standings board at
 * `/pools?poolId=<id>&view=standings`.
 *
 * `/standings/get` already groups rows by status (In, then Revived, then
 * Eliminated) and, inside the eliminated group, already orders them most
 * recently eliminated first (`eliminatedAtMs` is sort only server side and
 * is stripped before the response reaches here, so that order cannot be
 * recomputed and must be preserved verbatim). What the server does not do
 * is order the In and Revived groups by points; the server's own fallthrough
 * there compares `entryId`. That re-sort is this module's core job.
 *
 * This module is pure: no React import, no network, and no value import from
 * `@sportsmet/contest-sdk`. The one SDK import is `import type`, which is
 * erased at compile time, so it adds a type constraint and no runtime edge.
 * The row type is declared locally because `typeStandingsGetOutput200` is not
 * re-exported from `@sportsmet/contest-sdk/private`.
 */
import { VIEWER_CONTEST_STATUS_CALLED_OFF } from "@sportsmet/contest-engine";
import type { useContestList } from "@sportsmet/contest-sdk/private";

export const STANDINGS_TITLE = "Standings";
export const STANDINGS_BACK_LABEL = "Back to pool";
export const STANDINGS_LOADING_LABEL = "Loading standings";
export const STANDINGS_FAILED_MESSAGE = "We could not load these standings.";
export const STANDINGS_RETRY_LABEL = "Try again";
export const STANDINGS_EMPTY_HEADLINE = "No standings yet";
export const STANDINGS_EMPTY_BODY =
  "Standings appear once the first round is graded.";
export const STANDINGS_NO_CONTEST_HEADLINE = "No contest yet";
export const STANDINGS_NO_CONTEST_BODY = "This pool has not started a contest.";
export const STANDINGS_YOU_TAG = "(you)";
export const DELETED_ACCOUNT_NAME = "Deleted account";
export const DELETED_ACCOUNT_ENTRY_PREFIX = "Deleted account, Entry ";
export const STANDINGS_CARD_TITLE = "Standings";
export const STANDINGS_CARD_ACTION_LABEL = "View";

export type typeStandingsRow = {
  entryId: string;
  displayName: string;
  status: "in" | "eliminated" | "revived";
  livesRemaining: number;
  pointsTotal: number;
  rank: number;
  isViewer: boolean;
  // ISSUE-1160. The member's profile photo. Optional and nullable on the wire
  // (packages/contest-sdk/src/private/sdk/standings/postStandingsGet.ts).
  photoUrl?: string | null;
  // ISSUE-1318 and ISSUE-1377. Whose entry this is: a row opens that
  // member's rounds and the name links to their profile. Required on the
  // wire; optional here so a fixture without it still builds.
  userId?: string;
  // ISSUE-1327. Graded wins on this entry. Null when the server has none to
  // give, which the board shows as a dash, never as a guessed zero.
  wins?: number | null;
  losses?: number | null;
  // ISSUE-1327 (v1's "titles"). Medals this member earned this season, one
  // per contest won, from /standings/get. Optional so older fixtures build.
  medalsThisSeason?: number;
  // ISSUE-1348 (v1's "Revived" column). Times this entry was revived in this
  // contest, from /standings/get. Optional so older fixtures build.
  revivesThisContest?: number;
};

export type typeStandingsRowModel = typeStandingsRow & {
  isDeletedAccount: boolean;
  /** ISSUE-1377. Whose profile the name opens, or null when it opens none. */
  profileAccountId: string | null;
  initials: string;
  avatarUrl: string | null;
  statusLabel: string;
  livesCaption: string | null;
};

export type typeStandingsPhase =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "noContest" }
  | { phase: "empty" }
  // `rows` here is the API's raw, unsorted array. `buildStandingsRowModels`
  // (sort plus decoration) is applied where the board actually renders, so
  // that call is the one and only place the sort can be broken and caught.
  | { phase: "ready"; rows: readonly typeStandingsRow[] };

// ISSUE-864. `status` was bare `string` here, so none of the four literals in
// CONTEST_SELECTION_STATUS_ORDER was ever checked against the enum and a rename
// of a member would leave this file green while the board silently stopped
// selecting that contest. The status is now the SDK's own union, so a drift is
// a compile error. The rest of the row stays local, because
// `typeContestListOutput200` is not re-exported from
// `@sportsmet/contest-sdk/private`.
type typeContestStatus = NonNullable<
  NonNullable<ReturnType<typeof useContestList>["data"]>["data"]
>["items"][number]["status"];

type typeContestListItem = {
  contestId: string;
  status: typeContestStatus;
  startDate: string;
};

const CONTEST_SELECTION_STATUS_ORDER: readonly typeContestStatus[] = [
  "active",
  "pendingOutcome",
  "confirmed",
  "upcoming",
];

const STATUS_SORT_RANK = { in: 0, revived: 1, eliminated: 2 } as const;

export function selectStandingsContestId(
  result: { data?: null | { items: typeContestListItem[] } } | undefined,
  urlContestId: string | null,
): string | null {
  if (urlContestId !== null && urlContestId !== "") return urlContestId;
  const items = result?.data?.items ?? [];
  for (const status of CONTEST_SELECTION_STATUS_ORDER) {
    const candidates = items.filter((item) => item.status === status);
    if (candidates.length === 0) continue;
    const winner = candidates.reduce((best, candidate) =>
      candidate.startDate > best.startDate ? candidate : best,
    );
    return winner.contestId;
  }
  return null;
}

export function sortStandingsRows(
  rows: readonly typeStandingsRow[],
): typeStandingsRow[] {
  const decorated = rows.map((row, index) => ({ row, index }));
  decorated.sort((a, b) => {
    if (STATUS_SORT_RANK[a.row.status] !== STATUS_SORT_RANK[b.row.status]) {
      return STATUS_SORT_RANK[a.row.status] - STATUS_SORT_RANK[b.row.status];
    }
    if (a.row.status === "eliminated") {
      // Preserve the API's own order verbatim: the server has already put
      // eliminated rows most recently eliminated first, and the sort value
      // it used (eliminatedAtMs) is stripped before the response arrives.
      return a.index - b.index;
    }
    if (a.row.pointsTotal !== b.row.pointsTotal) {
      return b.row.pointsTotal - a.row.pointsTotal;
    }
    return a.index - b.index;
  });
  return decorated.map((entry) => entry.row);
}

export function isDeletedAccountName(displayName: string): boolean {
  return (
    displayName === DELETED_ACCOUNT_NAME ||
    displayName.startsWith(DELETED_ACCOUNT_ENTRY_PREFIX)
  );
}

export function initialsFor(displayName: string): string {
  if (isDeletedAccountName(displayName)) return "";
  const namePart = displayName.split(",")[0]?.trim() ?? "";
  const parts = namePart.split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0) return "";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

export function avatarUrlFor(row: {
  displayName: string;
  photoUrl?: string | null;
}): string | null {
  if (isDeletedAccountName(row.displayName)) return null;
  if (row.photoUrl === undefined || row.photoUrl === null) return null;
  if (row.photoUrl === "") return null;
  return row.photoUrl;
}

export function livesCaptionFor(row: {
  status: typeStandingsRow["status"];
  livesRemaining: number;
}): string | null {
  if (row.status === "eliminated") return null;
  return row.livesRemaining === 1
    ? "1 life left"
    : `${row.livesRemaining} lives left`;
}

export function statusLabelFor(status: typeStandingsRow["status"]): string {
  if (status === "in") return "In";
  if (status === "revived") return "Revived";
  return "Eliminated";
}

/**
 * ISSUE-1377. A name opens that member's profile, as in v1's standings. Your
 * own row and a deleted account's row open nothing: there is no other fan
 * behind either.
 */
export function profileAccountIdFor(row: typeStandingsRow): string | null {
  if (row.isViewer) return null;
  if (isDeletedAccountName(row.displayName)) return null;
  if (row.userId === undefined || row.userId === "") return null;
  return row.userId;
}

export function buildStandingsRowModels(
  rows: readonly typeStandingsRow[],
): typeStandingsRowModel[] {
  return sortStandingsRows(rows).map((row) => ({
    ...row,
    isDeletedAccount: isDeletedAccountName(row.displayName),
    profileAccountId: profileAccountIdFor(row),
    initials: initialsFor(row.displayName),
    avatarUrl: avatarUrlFor(row),
    statusLabel: statusLabelFor(row.status),
    livesCaption: livesCaptionFor(row),
  }));
}

export function resolveStandingsPhase(input: {
  isLoading: boolean;
  isFailed: boolean;
  contestId: string | null;
  entries: readonly typeStandingsRow[] | null;
}): typeStandingsPhase {
  if (input.isFailed) return { phase: "failed" };
  if (input.isLoading) return { phase: "loading" };
  if (input.contestId === null) return { phase: "noContest" };
  if (input.entries === null || input.entries.length === 0) {
    return { phase: "empty" };
  }
  return { phase: "ready", rows: input.entries };
}

// ISSUE-1327 and ISSUE-1348. The board's sort and status filter. Both run on
// the rows the server already sent and change only their order and which of
// them show. Neither decides a rank, a status or a point: those stay the
// server's, and the rank column always shows the server's rank.

export type typeStandingsSortKey =
  "rank" | "name" | "medals" | "revived" | "wins" | "points";
export type typeStandingsSortDirection = "asc" | "desc";
export type typeStandingsSort = {
  key: typeStandingsSortKey;
  direction: typeStandingsSortDirection;
};
export type typeStandingsStatusFilter = "all" | typeStandingsRow["status"];

export const DEFAULT_STANDINGS_SORT: typeStandingsSort = {
  key: "rank",
  direction: "asc",
};

export const STANDINGS_SORT_LABELS: Readonly<
  Record<typeStandingsSortKey, string>
> = {
  rank: "Rank",
  name: "Player",
  medals: "Medals",
  revived: "Revived",
  wins: "Wins",
  points: "Pts",
};

export const STANDINGS_SORT_GROUP_LABEL = "Sort standings";
export const STANDINGS_FILTER_GROUP_LABEL = "Filter standings by status";

export const STANDINGS_STATUS_FILTERS: readonly {
  value: typeStandingsStatusFilter;
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "in", label: "In" },
  { value: "revived", label: "Revived" },
  { value: "eliminated", label: "Eliminated" },
];

/** The empty line for a status filter nobody matches. */
export function standingsFilterEmptyLine(
  filter: typeStandingsStatusFilter,
): string {
  if (filter === "in") return "Nobody is still in right now.";
  if (filter === "revived") return "Nobody has been revived yet.";
  if (filter === "eliminated") return "Nobody has been eliminated yet.";
  return "Nobody is on this board yet.";
}

/**
 * The direction a column starts in the first time it is tapped. Rank and
 * name read top down; wins and points start with the most first, which is
 * what a fan tapping "Wins" is looking for.
 */
export function firstSortDirectionFor(
  key: typeStandingsSortKey,
): typeStandingsSortDirection {
  return key === "wins" ||
    key === "points" ||
    key === "medals" ||
    key === "revived"
    ? "desc"
    : "asc";
}

/** Tapping the active column flips it; tapping another column starts it fresh. */
export function nextStandingsSort(
  current: typeStandingsSort,
  key: typeStandingsSortKey,
): typeStandingsSort {
  if (current.key === key) {
    return { key, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { key, direction: firstSortDirectionFor(key) };
}

export function filterStandingsRows<
  TRow extends Pick<typeStandingsRow, "status">,
>(rows: readonly TRow[], filter: typeStandingsStatusFilter): TRow[] {
  if (filter === "all") return [...rows];
  return rows.filter((row) => row.status === filter);
}

/**
 * Re-orders rows that are already in the board's own order. "rank" keeps that
 * order as it is (ascending) or reverses it. Every other key is a stable sort
 * that falls back to the board's order on a tie, so two rows with the same
 * wins never swap places between renders. A row with no wins sorts last in
 * either direction, because "no number" is not a small number.
 */
export function applyStandingsSort<
  TRow extends Pick<
    typeStandingsRow,
    | "displayName"
    | "pointsTotal"
    | "wins"
    | "medalsThisSeason"
    | "revivesThisContest"
  >,
>(rows: readonly TRow[], sort: typeStandingsSort): TRow[] {
  const sign = sort.direction === "asc" ? 1 : -1;
  const decorated = rows.map((row, index) => ({ row, index }));
  if (sort.key === "rank") {
    return sort.direction === "asc"
      ? [...rows]
      : decorated.reverse().map((entry) => entry.row);
  }
  decorated.sort((a, b) => {
    if (sort.key === "name") {
      const byName = a.row.displayName.localeCompare(
        b.row.displayName,
        "en-US",
        { sensitivity: "base" },
      );
      if (byName !== 0) return byName * sign;
      return a.index - b.index;
    }
    if (sort.key === "medals") {
      const aMedals = a.row.medalsThisSeason ?? 0;
      const bMedals = b.row.medalsThisSeason ?? 0;
      if (aMedals !== bMedals) return (aMedals - bMedals) * sign;
      return a.index - b.index;
    }
    if (sort.key === "revived") {
      const aRevives = a.row.revivesThisContest ?? 0;
      const bRevives = b.row.revivesThisContest ?? 0;
      if (aRevives !== bRevives) return (aRevives - bRevives) * sign;
      return a.index - b.index;
    }
    if (sort.key === "wins") {
      const aWins = a.row.wins ?? null;
      const bWins = b.row.wins ?? null;
      if (aWins === null && bWins !== null) return 1;
      if (bWins === null && aWins !== null) return -1;
      if (aWins !== null && bWins !== null && aWins !== bWins) {
        return (aWins - bWins) * sign;
      }
      return a.index - b.index;
    }
    if (a.row.pointsTotal !== b.row.pointsTotal) {
      return (a.row.pointsTotal - b.row.pointsTotal) * sign;
    }
    return a.index - b.index;
  });
  return decorated.map((entry) => entry.row);
}

/**
 * The row's medal line ("1 medal", "3 medals"), or null when the member has
 * none this season, which keeps a phone row short (ISSUE-1327).
 */
export function medalsCaptionFor(row: {
  medalsThisSeason?: number;
}): string | null {
  const count = row.medalsThisSeason ?? 0;
  if (count <= 0) return null;
  return count === 1 ? "1 medal" : `${String(count)} medals`;
}

/**
 * The row's revive line ("1 revive", "2 revives"), or null for an entry that
 * was never revived, which keeps a phone row short (ISSUE-1348).
 */
export function revivesCaptionFor(row: {
  revivesThisContest?: number;
}): string | null {
  const count = row.revivesThisContest ?? 0;
  if (count <= 0) return null;
  return count === 1 ? "1 revive" : `${String(count)} revives`;
}

/**
 * The Revived sort only shows once somebody on the board has been revived;
 * before that every row reads zero and the column would sort nothing.
 */
export function hasAnyRevive(
  rows: readonly { revivesThisContest?: number }[],
): boolean {
  return rows.some((row) => (row.revivesThisContest ?? 0) > 0);
}

/** The wins cell. A dash when the server has no count, never a guessed 0. */
export function winsCellFor(row: { wins?: number | null }): string {
  return row.wins === undefined || row.wins === null ? "-" : String(row.wins);
}

// ISSUE-1331. The contest menu on the Results tab, shown once a season holds
// more than one contest. A called-off contest never ran, so it is not offered.

export const STANDINGS_CONTEST_LABEL = "Contest";

const CONTEST_STATUS_WORDS: Readonly<
  Partial<Record<typeContestStatus, string>>
> = {
  active: "in progress",
  pendingOutcome: "wrapping up",
  confirmed: "final",
  upcoming: "upcoming",
};

export type typeStandingsContestOption = { value: string; label: string };

export function buildContestOptions(
  items: readonly (typeContestListItem & { name: string })[],
): typeStandingsContestOption[] {
  return items
    .filter((item) => item.status !== VIEWER_CONTEST_STATUS_CALLED_OFF)
    .sort((a, b) => (a.startDate < b.startDate ? 1 : -1))
    .map((item) => {
      const word = CONTEST_STATUS_WORDS[item.status];
      return {
        value: item.contestId,
        label: word === undefined ? item.name : `${item.name} (${word})`,
      };
    });
}
